import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios'

const FALLBACK_API_URL = 'http://localhost:8000/api/v1/'

export const ACCESS_TOKEN_KEY = 'smartNyuki.token'
export const REFRESH_TOKEN_KEY = 'smartNyuki.refreshToken'

// Legacy keys that earlier builds wrote. Only cleared, never read.
const STALE_TOKEN_KEYS = ['access_token', 'refresh_token', 'auth.smartNyukiToken']

const SIGN_IN_PATH = '/auth/sign-in'

// Paths that must never trigger a token refresh: they either are the refresh
// itself (recursion) or are the endpoints you use precisely because you have
// no valid session yet, where a 401 means "bad credentials", not "expired".
const AUTH_ENTRY_PATHS = ['auth/refresh/', 'auth/login/', 'auth/register/', 'auth/google/']

export const tokenStorage = {
  getAccess: (): string | null => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefresh: (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY),
  set: (access: string, refresh?: string | null) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, access)
    if (refresh) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refresh)
    }
  },
  clear: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    STALE_TOKEN_KEYS.forEach((key) => localStorage.removeItem(key))
  },
}

/**
 * Pull a human-readable message out of an API error.
 *
 * Most endpoints reply with the wrapped {success, error: {message}, ...}
 * envelope, but DRF serializer failures are returned raw - a failed login is
 * {"non_field_errors": ["Invalid credentials"]}, and a rejected password is
 * {"new_password": ["This password is too short..."]}, neither of which has a
 * `message` key. Reading only `data.message` collapsed every one of those to
 * the same generic string, so the user never learned what went wrong.
 */
export const apiErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data
  if (!data) return error?.message || fallback

  if (typeof data === 'string') return data
  if (typeof data.message === 'string') return data.message
  if (typeof data.error?.message === 'string') return data.error.message
  if (typeof data.detail === 'string') return data.detail

  // DRF validation errors: non_field_errors first, then the first field error.
  const firstOf = (v: unknown) => (Array.isArray(v) ? v[0] : v)
  if (data.non_field_errors) return String(firstOf(data.non_field_errors))

  for (const [field, value] of Object.entries(data)) {
    const msg = firstOf(value)
    if (typeof msg === 'string') return `${field}: ${msg}`
  }

  return fallback
}

const normalizeBaseUrl = (rawUrl?: string): string => {
  const input = (rawUrl || FALLBACK_API_URL).trim()

  try {
    const parsed = new URL(input)
    let path = parsed.pathname || '/'

    if (path === '/' || path === '') {
      path = '/api/v1/'
    } else if (!path.includes('/api/v1')) {
      path = `${path.replace(/\/+$/, '')}/api/v1/`
    } else if (!path.endsWith('/')) {
      path = `${path}/`
    }

    parsed.pathname = path
    return parsed.toString()
  } catch {
    return FALLBACK_API_URL
  }
}

/**
 * Django resolves URLs with a trailing slash. Without one, APPEND_SLASH sends
 * a 301, and browsers downgrade a redirected POST/PUT/PATCH/DELETE to GET -
 * so the write silently does nothing. Normalising here means an endpoint
 * declared without the slash can't quietly lose data.
 */
const ensureTrailingSlash = (url: string): string => {
  const [pathAndQuery, hash] = url.split('#')
  const [path, query] = pathAndQuery.split('?')

  // Leave relative traversal and format suffixes (".json") alone.
  const lastSegment = path.split('/').pop() || ''
  if (path === '' || path.endsWith('/') || lastSegment.includes('.')) {
    return url
  }

  const withSlash = `${path}/`
  return `${withSlash}${query ? `?${query}` : ''}${hash ? `#${hash}` : ''}`
}

const normalizeApiPath = (url?: string): string | undefined => {
  if (!url) {
    return url
  }

  // Preserve absolute URLs and strip leading slashes for API-relative paths.
  if (/^https?:\/\//i.test(url)) {
    return url
  }

  return ensureTrailingSlash(url.replace(/^\/+/, ''))
}

const isAuthEntryRequest = (url?: string): boolean => {
  if (!url) return false
  return AUTH_ENTRY_PATHS.some((path) => url.includes(path))
}

const redirectToSignIn = () => {
  if (typeof window !== 'undefined' && window.location.pathname !== SIGN_IN_PATH) {
    window.location.href = SIGN_IN_PATH
  }
}

// A bare instance for the refresh call, so refreshing can never re-enter the
// response interceptor below and recurse.
const refreshClient = axios.create({
  baseURL: normalizeBaseUrl(import.meta.env.VITE_API_URL),
  timeout: 30000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
})

// Single-flight: many requests can 401 at once, but only one refresh should
// run. The rest await this promise and then retry with the new token.
let refreshInFlight: Promise<string> | null = null

const refreshAccessToken = (): Promise<string> => {
  if (refreshInFlight) {
    return refreshInFlight
  }

  const refresh = tokenStorage.getRefresh()
  if (!refresh) {
    return Promise.reject(new Error('No refresh token available'))
  }

  refreshInFlight = refreshClient
    .post('auth/refresh/', { refresh })
    .then((response) => {
      // Backend wraps auth responses as { success, data, message }.
      const payload = response.data?.data ?? response.data
      const access: string | undefined = payload?.access

      if (!access) {
        throw new Error('Refresh response contained no access token')
      }

      // ROTATE_REFRESH_TOKENS is on, so the old refresh token is now
      // blacklisted and the new one must replace it.
      tokenStorage.set(access, payload?.refresh)
      return access
    })
    .finally(() => {
      refreshInFlight = null
    })

  return refreshInFlight
}

// Create axios instance with base configuration following SmartNyuki patterns
const createApiInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: normalizeBaseUrl(import.meta.env.VITE_API_URL),
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  })

  // Request interceptor to add auth token (following SmartNyuki pattern)
  instance.interceptors.request.use(
    (config) => {
      config.url = normalizeApiPath(config.url)

      const token = tokenStorage.getAccess()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }

      // Add locale support like SmartNyuki
      const locale = localStorage.getItem('locale') || 'en'
      config.headers['Accept-Language'] = locale

      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // Response interceptor: refresh the access token once on 401, then replay
  // the original request. Only give up (and sign the user out) if refreshing
  // itself fails.
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as
        | (InternalAxiosRequestConfig & { _retry?: boolean })
        | undefined
      const status = error.response?.status

      if (status !== 401 || !originalRequest || originalRequest._retry) {
        if (status === 401 && originalRequest?._retry) {
          tokenStorage.clear()
          redirectToSignIn()
        }
        return Promise.reject(error)
      }

      if (isAuthEntryRequest(originalRequest.url) || !tokenStorage.getRefresh()) {
        // Nothing to refresh with - a 401 here is terminal.
        if (!isAuthEntryRequest(originalRequest.url)) {
          tokenStorage.clear()
          redirectToSignIn()
        }
        return Promise.reject(error)
      }

      originalRequest._retry = true

      try {
        const access = await refreshAccessToken()
        originalRequest.headers.Authorization = `Bearer ${access}`
        return instance(originalRequest)
      } catch (refreshError) {
        tokenStorage.clear()
        redirectToSignIn()
        return Promise.reject(refreshError)
      }
    }
  )

  return instance
}

export const apiClient = createApiInstance()

// SmartNyuki-style API service class
export class ApiService {
  // Standard CRUD operations
  static async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return apiClient.get(url, config)
  }

  static async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return apiClient.post(url, data, config)
  }

  static async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return apiClient.put(url, data, config)
  }

  static async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return apiClient.patch(url, data, config)
  }

  static async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return apiClient.delete(url, config)
  }

  // File upload with proper headers
  static async upload<T = any>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return apiClient.post(url, formData, {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  // SmartNyuki-style convenience methods
  static async readRequest<T = any>(url: string, id: string | number = ''): Promise<AxiosResponse<T>> {
    return apiClient.get(`${url}${id}`)
  }

  static async postRequest<T = any>(url: string, payload: any = null): Promise<AxiosResponse<T>> {
    return apiClient.post(url, payload)
  }

  static async updateRequest<T = any>(url: string, id: string | number, payload: any): Promise<AxiosResponse<T>> {
    return apiClient.patch(`${url}${id}/`, payload)
  }

  static async deleteRequest<T = any>(url: string, id: string | number, payload: any = null): Promise<AxiosResponse<T>> {
    return apiClient.delete(`${url}${id}/`, { data: payload })
  }

  // Health check
  static async checkConnection(): Promise<AxiosResponse> {
    // Relative traversal from /api/v1/ to /api/health/.
    return apiClient.get('../health/')
  }
}

// Export both for compatibility
export const api = apiClient
export default apiClient
